import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Briefcase, Scale, Landmark, Quote, MapPin, BadgeInfo, Award, Phone } from "lucide-react";
import api from "@/lib/api";
import { useSettings, telHref } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";
import { resolveImg } from "@/lib/util";
import { PublicLayout } from "@/components/site/PublicLayout";

function Section({ icon: Icon, label, title, children, testid }) {
  return (
    <div className="mb-14" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-9 h-9 rounded-sm bg-navy text-brass flex items-center justify-center"><Icon size={17} /></span>
        <p className="text-brass font-mono text-xs tracking-[0.2em] uppercase">{label}</p>
      </div>
      <h2 className="font-serif text-2xl lg:text-3xl text-navy mb-6">{title}</h2>
      <div className="text-slate-600 leading-relaxed text-base lg:text-lg">{children}</div>
    </div>
  );
}

const isLegal = (q) => q?.legal || /ll\.?b|law/i.test(`${q?.qualification || ""} ${q?.field || ""}`);

export default function About() {
  const settings = useSettings();
  const [areas, setAreas] = useState([]);
  useEffect(() => { api.get("/practice-areas").then((r) => setAreas(r.data)); }, []);

  const p = settings?.profile || {};
  const c = settings?.contact || {};
  const quals = p.qualifications || [];
  const exps = p.experiences || [];

  useSeo({
    title: "Adv. Guruvayoorappan K V | Advocate in Thrissur, Kerala",
    description: "Learn about Adv. Guruvayoorappan K V, Advocate based in Thrissur, Kerala, including his educational qualifications, professional experience and areas of legal practice.",
    ogImage: resolveImg(p.portrait_url),
    jsonLd: {
      "@context": "https://schema.org", "@type": "Person", name: "Adv. Guruvayoorappan K V", jobTitle: "Advocate",
      description: "Advocate practicing as a Counsel since 2017; former Central Government Additional Standing Counsel for three years.",
      hasCredential: quals.map((q) => ({ "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: [q.qualification, q.field].filter(Boolean).join(" ") })),
      worksFor: { "@type": "LegalService", name: "Adv. Guruvayoorappan K V" },
      address: { "@type": "PostalAddress", addressLocality: "Thrissur", addressRegion: "Kerala", addressCountry: "IN" },
    },
  });

  return (
    <PublicLayout>
      {/* Profile Hero with transparent cut-out */}
      <section className="relative bg-navy overflow-hidden" data-testid="profile-header">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-10 items-end pt-14 lg:pt-20 relative">
          <div className="pb-14 lg:pb-20 order-2 lg:order-1">
            <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Profile</p>
            <h1 className="font-serif text-4xl lg:text-5xl text-ivory leading-tight mb-2">ADV. GURUVAYOORAPPAN K V</h1>
            <p className="text-brass text-lg font-serif italic mb-1">Advocate</p>
            <p className="text-ivory/50 text-sm mb-6">Thrissur, Kerala</p>
            <p className="text-ivory/70 leading-relaxed max-w-2xl mb-8">{p.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="bg-brass hover:bg-brass-hover text-navy font-semibold px-6 py-3 rounded-sm transition-colors inline-flex items-center gap-2">Request a Consultation <ArrowRight size={16} /></Link>
              <a href={telHref(c.phone1)} className="border border-ivory/25 hover:border-brass text-ivory hover:text-brass font-semibold px-6 py-3 rounded-sm transition-colors inline-flex items-center gap-2"><Phone size={16} /> Call Now</a>
            </div>
          </div>
          <div className="relative order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="absolute bottom-0 w-72 h-72 bg-gradient-to-t from-brass/25 to-transparent rounded-full blur-3xl" />
            {p.portrait_url && (
              <img src={resolveImg(p.portrait_url)} alt="Adv. Guruvayoorappan K V, Advocate" className="relative z-10 w-auto max-h-[420px] lg:max-h-[520px] object-contain drop-shadow-2xl" data-testid="profile-portrait" />
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        {/* Introduction */}
        <Section icon={Scale} label="Introduction" title="Professional Introduction" testid="section-intro">
          <p>{p.intro}</p>
        </Section>

        {/* Education */}
        <Section icon={GraduationCap} label="Academic Background" title="Education & Qualifications" testid="section-education">
          {quals.length === 0 ? <p className="text-slate-400">[Add Educational Qualification]</p> : (
            <div className="grid sm:grid-cols-2 gap-5">
              {quals.map((q, i) => (
                <div key={i} className={`relative rounded-lg p-6 border transition-all hover:shadow-lg ${isLegal(q) ? "bg-navy text-ivory border-brass" : "bg-white border-slate-200"}`} data-testid={`qual-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className={`w-11 h-11 rounded-sm flex items-center justify-center shrink-0 ${isLegal(q) ? "bg-brass text-navy" : "bg-navy/5 text-navy"}`}>
                      {isLegal(q) ? <Scale size={20} /> : <GraduationCap size={20} />}
                    </span>
                    {isLegal(q) && <span className="text-[10px] bg-brass text-navy font-semibold px-2 py-1 rounded-full uppercase tracking-wide">Legal Qualification</span>}
                  </div>
                  <h3 className={`font-serif text-2xl mt-4 mb-1 ${isLegal(q) ? "text-ivory" : "text-navy"}`}>{q.qualification}</h3>
                  {q.field && <p className={`text-sm ${isLegal(q) ? "text-brass" : "text-slate-500"}`}>{q.field}</p>}
                  {(q.institution || q.year) && <p className={`text-xs mt-2 ${isLegal(q) ? "text-ivory/60" : "text-slate-400"}`}>{[q.institution, q.year].filter(Boolean).join(" · ")}</p>}
                  {q.description && <p className={`text-sm mt-3 leading-relaxed ${isLegal(q) ? "text-ivory/70" : "text-slate-500"}`}>{q.description}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Experience */}
        <Section icon={Briefcase} label="Career" title="Professional Experience" testid="section-experience">
          {exps.length === 0 ? <p className="text-slate-400">[Add Professional Experience]</p> : (
            <div className="relative pl-8 border-l-2 border-brass/30">
              {exps.map((e, i) => (
                <div key={i} className="relative pb-10 last:pb-0" data-testid={`exp-item-${i}`}>
                  <span className="absolute -left-[41px] top-1 w-5 h-5 rounded-full bg-brass border-4 border-ivory" />
                  <div className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    {(e.start_year || e.end_year) && <p className="text-brass font-mono text-xs tracking-widest uppercase mb-2">{[e.start_year, e.end_year].filter(Boolean).join(" – ")}</p>}
                    <h3 className="font-serif text-xl text-navy">{e.role}</h3>
                    {e.organization && <p className="text-slate-500 text-sm mt-0.5">{e.organization}</p>}
                    {e.description && <p className="text-slate-600 mt-3 leading-relaxed">{e.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Areas of Practice */}
        <Section icon={Landmark} label="Practice" title="Areas of Practice" testid="section-areas">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {areas.map((a) => (
              <Link key={a.id} to={`/practice-areas/${a.slug}`} className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3 hover:border-brass transition-colors text-navy">
                <span className="text-sm font-medium">{a.title}</span>
                <ArrowRight size={15} className="text-brass" />
              </Link>
            ))}
          </div>
        </Section>

        {/* Approach */}
        {settings?.approach?.length > 0 && (
          <Section icon={BadgeInfo} label="How We Work" title="Professional Approach" testid="section-approach">
            <div className="grid sm:grid-cols-2 gap-6">
              {settings.approach.map((a, i) => (
                <div key={i} className="border-t-2 border-brass pt-4">
                  <span className="font-serif text-2xl text-brass/40">0{i + 1}</span>
                  <h3 className="font-serif text-lg text-navy mt-1 mb-1">{a.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Thought */}
        {p.thought && (
          <div className="my-14 bg-ivory-dark border-l-4 border-brass rounded-r-lg p-8" data-testid="section-thought">
            <Quote size={32} className="text-brass mb-4" />
            <p className="text-brass font-mono text-xs tracking-[0.2em] uppercase mb-3">Thought on Law & Society</p>
            <blockquote className="font-serif text-xl lg:text-2xl text-navy italic leading-relaxed">"{p.thought}"</blockquote>
          </div>
        )}

        {/* Location + Info */}
        <Section icon={MapPin} label="Location" title="Practice Location" testid="section-location">
          <p className="whitespace-pre-line">{p.practice_location || c.address}</p>
        </Section>

        {/* Contact CTA */}
        <div className="bg-navy rounded-lg p-8 lg:p-10 text-center" data-testid="profile-cta">
          <h2 className="font-serif text-2xl lg:text-3xl text-ivory mb-3">Discuss Your Legal Matter</h2>
          <p className="text-ivory/60 mb-6 max-w-xl mx-auto">Submit an enquiry to discuss your matter, subject to jurisdiction, facts and professional availability.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brass hover:bg-brass-hover text-navy font-semibold px-8 py-3.5 rounded-sm transition-colors">Request a Consultation <ArrowRight size={17} /></Link>
        </div>
      </div>
    </PublicLayout>
  );
}
